import { Maybe } from '@paradigm/utils/types';
import { formatProductPrice } from '#/unified-rfqs/ui/rfqs/rfqs-functions';
import { RfqType, Side, getOppositeSide } from '#/unified-rfqs/entities/shared';
import { findBaseProductCode } from '#/products/domain';

import { GrfqQuote } from '#/unified-rfqs/entities/grfq/domain';
import { isTaker as isDrfqTaker } from '@paradigm/features/src/unified-rfqs/entities/drfq/domain';
import { Drfq } from '#/unified-rfqs/entities/drfq/domain';
import {
  formatCustomNonIntegerPrice,
  isCustomNonIntegerRatio,
  mostPrecisePricePrecision,
} from '#/unified-rfqs/utils/rfq-price-functions';
import {
  primaryProductCode,
  strategyProductCodes,
} from '#/unified-rfqs/utils/legs';

interface Args {
  price: Maybe<string>;
  productCode: Maybe<string>;
  side: Maybe<Side>;
  rfqType: Maybe<RfqType>;
  description: Maybe<string>;
  precisionOverride?: number;
}

export default function useFormattedPrice({ price, ...rest }: Args): string {
  const formatter = usePriceFormatterFn(rest);

  return formatter(price);
}

export function usePriceFormatterFn({
  productCode,
  side,
  rfqType,
  description,
  precisionOverride,
}: Omit<Args, 'price'>) {
  const defaultFormater = (price: Maybe<string>) =>
    formatProductPrice(price ?? null, productCode ?? undefined);

  const isCustomNonInteger = isCustomNonIntegerRatio(description);
  if (!isCustomNonInteger || productCode == null) {
    return defaultFormater;
  }

  switch (rfqType) {
    case 'grfq':
    case 'drfq':
      return (price: Maybe<string>, customSide?: Maybe<Side>) =>
        formatCustomNonIntegerPrice(
          price,
          productCode,
          customSide ?? side,
          precisionOverride,
        );
    default:
      return defaultFormater;
  }
}

export function useFormattedGrfqQuotePrice(
  quote: Maybe<GrfqQuote>,
  // Defaults to the quote's premium price; pass the net price for a
  // hedged RFQ so the display matches the net mark.
  price: Maybe<string> = quote?.price,
) {
  return useFormattedPrice({
    price,
    productCode: quote?.product_codes?.[0],
    side: quote?.side,
    rfqType: 'grfq',
    description: quote?.description,
  });
}

/**
 * Formats GRFQ price considering the role, always show the MAKER side price.
 */
export function useFormattedGrfqMakerPrice({
  price,
  side,
  productCode,
  description,
  isTaker,
}: Omit<Args, 'rfqType'> & { isTaker: Maybe<boolean> }) {
  const makerPriceSide =
    isTaker === true && side != null ? getOppositeSide(side) : side;

  return useFormattedPrice({
    price,
    productCode,
    side: makerPriceSide,
    rfqType: 'grfq',
    description,
  });
}

type DrfqInfo = Pick<Drfq, 'role' | 'product_codes' | 'legs' | 'description'>;
interface DrfqFormattedPrice {
  price: Maybe<string>;
  side: Maybe<Side>;
  drfqInfo: Maybe<DrfqInfo>;
}

/**
 * Formats executed price of drfq considering the user role.
 * We should always display the same price submitted by the MAKER,
 * so we invert the side if the user is a taker.
 */
export function useFormattedDrfqMakerPrice({
  price,
  side,
  drfqInfo,
}: DrfqFormattedPrice) {
  const { product_codes = [], legs = [], description } = drfqInfo ?? {};

  // Same resolution the BBO of this package uses: without it the two
  // would round to different places on a multi-product package, and
  // they are rendered side by side to be compared. The precision
  // override below only reaches the custom non-integer ratio path.
  const baseLegProductCode =
    primaryProductCode(legs) ?? findBaseProductCode(product_codes);
  const makerPriceSide =
    drfqInfo != null && side != null && isDrfqTaker(drfqInfo)
      ? getOppositeSide(side)
      : side;
  // product_codes is [strategy_product_code, hedge_product_code] for a
  // hedged RFQ, so precision is resolved from the strategy legs alone —
  // blending in the hedge future's own price precision would be wrong.
  // The price is served net of the hedge; a linear hedge's `price ×
  // ratio` term rounds to the option's precision, so the displayed net
  // can differ from premium + hedge by <1 tick (inverse is exact).
  const precisionOverride = mostPrecisePricePrecision(
    strategyProductCodes(legs),
    true,
  );

  return useFormattedPrice({
    price,
    side: makerPriceSide,
    productCode: baseLegProductCode,
    rfqType: 'drfq',
    description,
    precisionOverride,
  });
}
